// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import axios from "axios";
import { keycloakInstance, initKeycloakOnce } from "../config/keycloak";
import env from "../utils/env";

const TOKEN_REFRESH_MIN_VALIDITY = 70;
const REQUEST_REFRESH_THRESHOLD_SEC = 30;

const apiClient = axios.create({
  baseURL: env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

let refreshPromise = null;

const refreshToken = () => {
  if (!refreshPromise) {
    refreshPromise = keycloakInstance
      .updateToken(TOKEN_REFRESH_MIN_VALIDITY)
      .then(() => keycloakInstance.token)
      .catch((err) => {
        keycloakInstance.logout();
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const isTokenAboutToExpire = () => {
  const tokenExp = keycloakInstance.tokenParsed?.exp;
  if (!tokenExp) return false;
  const secondsLeft = tokenExp - Math.floor(Date.now() / 1000);
  return secondsLeft < REQUEST_REFRESH_THRESHOLD_SEC;
};

apiClient.interceptors.request.use(async (config) => {
  try {
    await initKeycloakOnce();
  } catch {
    // init failed, let the request proceed; it will 401 and be handled below.
  }

  if (keycloakInstance.authenticated && isTokenAboutToExpire()) {
    try {
      await refreshToken();
    } catch (err) {
      return Promise.reject(err);
    }
  }

  const token = keycloakInstance.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status !== 401 || originalRequest?._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    try {
      const token = await refreshToken();
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  },
);

export default apiClient;
